import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDemo } from "@/context/DemoContext";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { Plus, Save, Lock, Trash2, Printer, Paperclip, CheckCircle2, XCircle, RotateCcw, Ban, Upload, Download, AlertTriangle, ArrowLeft, FileText } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime, fmtCurrency, statoClass, FASI_NOMI, faseClass } from "@/lib/helpers";
import FeedbackButton from "@/components/FeedbackButton";
import Stepper from "@/components/Stepper";
import TicketInfoBar from "@/components/TicketInfoBar";
import jsPDF from "jspdf";

const TARIFFE = {
  Italia: { work_sw: 80, work_hw: 95, travel: 45, km: 0.5, pernotto: 90, pranzo: 25, cena: 30 },
  Estero: { work_sw: 110, work_hw: 130, travel: 60, km: 0.6, pernotto: 130, pranzo: 30, cena: 40 },
};

const parseNum = (s) => { const t = String(s ?? "").replace(/\s/g, ""); return parseFloat(t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t) || 0; };
const fmtNumIt = (v) => (v ? Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "");

function BufferedInput({ value, onSave, numeric, ...props }) {
  const norm = (v) => (numeric ? fmtNumIt(v) : (v ?? ""));
  const [buf, setBuf] = useState(norm(value));
  useEffect(() => { setBuf(norm(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = () => {
    const out = numeric ? parseNum(buf) : buf;
    if (out !== (numeric ? Number(value || 0) : (value ?? ""))) onSave(out);
  };
  return <Input {...props} type={numeric ? "text" : props.type} inputMode={numeric ? "decimal" : undefined} value={buf}
    onChange={e => setBuf(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }} />;
}

function SignaturePad({ onChange, value }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(!value);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2; ctx.strokeStyle = "#0f172a"; ctx.lineCap = "round";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
    }
  }, [value]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setEmpty(false); };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); } };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0,0,c.width,c.height); setEmpty(true); onChange(""); };

  return (
    <div>
      <canvas ref={canvasRef} width={520} height={140} className="signature-canvas w-full max-w-full"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas" />
      <div className="flex items-center justify-between mt-1 text-xs">
        <span className="text-slate-500">Firma dimostrativa — nessun valore legale</span>
        <button className="text-blue-700 hover:underline" onClick={clear} type="button" data-testid="signature-clear">Cancella</button>
      </div>
    </div>
  );
}

// Campi contatto Fase 1 Apertura — buffer locali per evitare drop di caratteri in digitazione rapida.
function ContattoF1Fields({ ticket, contatto, editableGen, saveTicket }) {
  const legacyNome = contatto?.nome || "";
  const legacyEmail = contatto?.email || "";
  const legacyTel = contatto?.telefono || "";
  const [nomeBuf, setNomeBuf] = useState(ticket.contatto_nome ?? legacyNome);
  const [emailBuf, setEmailBuf] = useState(ticket.contatto_email ?? legacyEmail);
  const [telBuf, setTelBuf] = useState(ticket.contatto_telefono ?? legacyTel);
  useEffect(() => { setNomeBuf(ticket.contatto_nome ?? legacyNome); }, [ticket.id, ticket.contatto_nome]);
  useEffect(() => { setEmailBuf(ticket.contatto_email ?? legacyEmail); }, [ticket.id, ticket.contatto_email]);
  useEffect(() => { setTelBuf(ticket.contatto_telefono ?? legacyTel); }, [ticket.id, ticket.contatto_telefono]);
  return (
    <>
      <div><Label>Contatto / Richiedente</Label>
        <Input
          value={nomeBuf}
          onChange={e => setNomeBuf(e.target.value)}
          onBlur={() => { if (nomeBuf !== (ticket.contatto_nome ?? legacyNome)) saveTicket({ contatto_nome: nomeBuf }); }}
          disabled={!editableGen}
          placeholder="Es. Mario Rossi · ufficio tecnico"
          data-testid="f1-contatto"
        />
        <div className="text-xs text-slate-500 mt-1">
          Testo libero — potrà poi essere aggiunto all'anagrafica contatti.
          {contatto && !ticket.contatto_nome && <> · Riferimento storico: {contatto.ruolo}</>}
        </div>
      </div>
      <div><Label>Email contatto</Label>
        <Input
          type="email"
          value={emailBuf}
          onChange={e => setEmailBuf(e.target.value)}
          onBlur={() => { if (emailBuf !== (ticket.contatto_email ?? legacyEmail)) saveTicket({ contatto_email: emailBuf }); }}
          disabled={!editableGen}
          placeholder="mario.rossi@azienda.it"
          data-testid="f1-contatto-email"
        />
        <div className="text-xs text-slate-500 mt-1">Facoltativa — usata nelle notifiche ai tecnici assegnati.</div>
      </div>
      <div><Label>Telefono contatto</Label>
        <Input
          value={telBuf}
          onChange={e => setTelBuf(e.target.value)}
          onBlur={() => { if (telBuf !== (ticket.contatto_telefono ?? legacyTel)) saveTicket({ contatto_telefono: telBuf }); }}
          disabled={!editableGen}
          placeholder="+39 ..."
          data-testid="f1-contatto-telefono"
        />
        <div className="text-xs text-slate-500 mt-1">Facoltativo — usato nelle notifiche ai tecnici assegnati.</div>
      </div>
    </>
  );
}

function FaseHeader({ ticket, num, onFaseChange, canEdit }) {
  const f = ticket.fasi?.find(x => x.num === num);
  const [motivo, setMotivo] = useState(f?.motivo_na || "");
  if (!f) return null;
  const setStato = async (stato) => {
    if (stato === "Non applicabile" && !motivo.trim()) { toast.error("Motivazione richiesta per 'Non applicabile'"); return; }
    await onFaseChange(num, { stato, motivo_na: stato === "Non applicabile" ? motivo : "" });
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-4 bg-slate-50 border rounded-lg p-3">
      <div>
        <div className="text-xs text-slate-500 uppercase">Fase {num} di 8</div>
        <div className="font-semibold text-slate-900">{FASI_NOMI[num-1]} · <span className={`fase-pill ${faseClass(f.stato)}`}>{f.stato}</span></div>
        {f.stato === "Non applicabile" && f.motivo_na && <div className="text-xs text-slate-600 mt-1">Motivazione: {f.motivo_na}</div>}
      </div>
      {canEdit && (
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={f.stato} onValueChange={setStato}>
            <SelectTrigger className="w-[170px]" data-testid={`fase-${num}-stato`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Da fare">Da fare</SelectItem>
              <SelectItem value="In lavorazione">In lavorazione</SelectItem>
              <SelectItem value="Completata">Completata</SelectItem>
              {num > 2 && <SelectItem value="Non applicabile">Non applicabile</SelectItem>}
            </SelectContent>
          </Select>
          {f.stato !== "Non applicabile" && num > 2 && (
            <Input placeholder="Motivo N/A (facoltativo)" value={motivo} onChange={e => setMotivo(e.target.value)} className="w-[220px]" />
          )}
        </div>
      )}
    </div>
  );
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data, reload, canEdit, canClose, isTecnico, myTecnicoId, user } = useDemo();
  const profile = { nome: user?.nome || "", perm: user?.role || "" };
  const [ticket, setTicket] = useState(null);
  const [interventi, setInterventi] = useState([]);
  const [rapporti, setRapporti] = useState([]);
  const [consuntivi, setConsuntivi] = useState([]);
  const [conferme, setConferme] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [tab, setTab] = useState("f1");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = async () => {
    const [t, iv, rp, cn, cf, ft, dc] = await Promise.all([
      api.get(`/tickets/${id}`),
      api.get(`/interventi?ticket_id=${id}`),
      api.get(`/rapporti?ticket_id=${id}`),
      api.get(`/consuntivi?ticket_id=${id}`),
      api.get(`/conferme?ticket_id=${id}`),
      api.get(`/fatture?ticket_id=${id}`),
      api.get(`/documenti?ticket_id=${id}`),
    ]);
    setTicket(t); setInterventi(iv); setRapporti(rp); setConsuntivi(cn); setConferme(cf); setFatture(ft); setDocumenti(dc);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!ticket) return <div className="p-8 text-slate-500">Caricamento…</div>;

  const cliente = data.clienti.find(c => c.id === ticket.cliente_id);
  const stab = data.stabilimenti.find(s => s.id === ticket.stabilimento_id);
  const contatto = data.contatti.find(c => c.id === ticket.contatto_id);
  const paese = stab?.paese || "Italia";
  const isEstero = paese !== "Italia";
  const isViewer = user?.role === "VIEWER";
  const editableGen = canEdit("ticket") && ticket.stato !== "Chiuso" && !isViewer;
  const editableFatt = canEdit("fatturazione") && !isViewer;

  const sumCons = consuntivi.filter(c => c.stato === "confermato").reduce((s,c) => s + (c.righe||[]).reduce((a,r)=>a+Number(r.totale||0),0), 0);
  const sumFatt = fatture.reduce((s,f) => s + Number(f.importo||0), 0);
  const totConsuntivo = sumCons || Number(ticket.importo_consuntivo_manuale || 0);
  const totFatturato = sumFatt || Number(ticket.importo_fatturato_manuale || 0);
  const statoFatt = totFatturato === 0 ? "Non fatturato" : totFatturato < totConsuntivo ? "Parzialmente fatturato" : "Fatturato";

  const saveTicket = async (patch) => {
    const updated = await api.put(`/tickets/${id}`, patch);
    setTicket(updated); toast.success("Salvato");
  };

  const changeFase = async (num, patch) => {
    await api.put(`/tickets/${id}/fase/${num}`, patch);
    await load(); toast.success(`Fase ${num} aggiornata`);
  };

  // ================= TABS =================
  const tabs = [
    { id: "f1", label: "1 · Apertura" },
    { id: "f2", label: "2 · Triage" },
    { id: "f3", label: "3 · Assegnazione" },
    { id: "f4", label: "4 · Dati intervento" },
    { id: "f5", label: "5 · Consuntivo" },
    { id: "f6", label: "6 · Conferma cliente" },
    { id: "f7", label: "7 · Fatturazione" },
    { id: "f8", label: "8 · Chiusura" },
    { id: "agg", label: "Aggiornamenti" },
    { id: "doc", label: "Documenti" },
  ];

  // Tecnico: force f4
  const visibleTabs = isTecnico() ? tabs.filter(t => t.id === "f4" || t.id === "doc") : tabs;

  return (
    <div className="p-4 md:p-6 max-w-7xl">
      <Link to="/ticket" className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Torna al cruscotto</Link>

      {/* Info bar top */}
      <TicketInfoBar ticket={ticket} cliente={cliente} stabilimento={stab} extra={[
        (ticket.divisioni_coinvolte || []).length > 0 && { label: "Divisioni", value: <span className="text-sm font-semibold text-blue-800">{(ticket.divisioni_coinvolte || []).join(", ")}</span> },
        { label: "Stato", value: <span className={`stato-pill ${statoClass(ticket.stato)}`}>{ticket.stato}</span> },
      ].filter(Boolean)} />

      {/* Horizontal stepper */}
      <div className="card-clean p-4 mt-3 mb-4">
        <Stepper fasi={ticket.fasi || []} currentFase={parseInt(tab.replace("f","")) || 1} />
      </div>

      {/* Header actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {ticket.urgente && <Badge className="bg-red-600 hover:bg-red-700">● URGENTE</Badge>}
          {ticket.bozza && <Badge variant="outline" className="text-amber-700 border-amber-300">Bozza incompleta</Badge>}
          <span className="text-xs text-slate-500">Aperto il {fmtDateTime(ticket.created_at)}</span>
        </div>
        <div className="flex items-center gap-2">
          {(user?.role === "OWNER" || user?.role === "SERVICE_MANAGER") && (
            <Button
              size="sm"
              variant="outline"
              className="text-red-700 border-red-300 hover:bg-red-50 hover:text-red-800"
              onClick={() => setConfirmDelete(true)}
              data-testid="btn-delete-ticket"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Elimina ticket
            </Button>
          )}
          <FeedbackButton schermata={`Ticket · ${ticket.numero}`} ticketId={ticket.numero} />
        </div>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent data-testid="delete-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Elimina ticket {ticket.numero}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p>Verranno eliminati definitivamente:</p>
            <ul className="list-disc pl-5 text-slate-700">
              <li>Il ticket <strong>{ticket.numero}</strong></li>
              <li>Tutti gli interventi, rapporti, consuntivi, conferme, fatture e documenti collegati</li>
            </ul>
            <p className="text-red-700 font-semibold">L'operazione non è reversibile.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)} data-testid="btn-delete-cancel">Annulla</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                try {
                  await api.del(`/tickets/${id}`);
                  toast.success(`Ticket ${ticket.numero} eliminato`);
                  setConfirmDelete(false);
                  await reload();
                  nav("/ticket");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Errore durante l'eliminazione");
                }
              }}
              data-testid="btn-delete-confirm"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto justify-start bg-white border card-clean p-1">
          {visibleTabs.map(t => <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`} className="data-[state=active]:bg-blue-700 data-[state=active]:text-white">{t.label}</TabsTrigger>)}
        </TabsList>

        {/* ===== F1 Apertura ===== */}
        <TabsContent value="f1"><Card className="card-clean p-5 mt-3">
          <FaseHeader ticket={ticket} num={1} onFaseChange={changeFase} canEdit={editableGen} />
          <div className="section-h">Identificazione richiesta</div>
          <div className="grid md:grid-cols-3 gap-3 mb-6">
            <div><Label>Cliente *</Label>
              <Combobox
                value={ticket.cliente_id || ""}
                onChange={v => saveTicket({ cliente_id: v, stabilimento_id: "" })}
                options={data.clienti.map(c => ({ value: c.id, label: c.ragione_sociale }))}
                placeholder="Seleziona cliente…"
                searchPlaceholder="Cerca cliente…"
                emptyText="Nessun cliente trovato"
                disabled={!editableGen}
                data-testid="f1-cliente"
              />
            </div>
            <div><Label>Stabilimento *</Label>
              <Combobox
                value={ticket.stabilimento_id || ""}
                onChange={v => saveTicket({ stabilimento_id: v })}
                options={data.stabilimenti.filter(s => s.cliente_id === ticket.cliente_id).map(s => ({ value: s.id, label: `${s.nome} (${s.paese})` }))}
                placeholder={ticket.cliente_id ? "Seleziona stabilimento…" : "Prima scegli il cliente"}
                searchPlaceholder="Cerca stabilimento…"
                emptyText="Nessuno stabilimento disponibile"
                disabled={!editableGen || !ticket.cliente_id}
                data-testid="f1-stabilimento"
              />
            </div>
            <ContattoF1Fields ticket={ticket} contatto={contatto} editableGen={editableGen} saveTicket={saveTicket} />
          </div>

          <div className="section-h">Segnalazione</div>
          <div className="mb-6">
            <Label>Oggetto sintetico</Label>
            <Input
              value={ticket.oggetto||""}
              onChange={e => setTicket({ ...ticket, oggetto: e.target.value })}
              onBlur={e => saveTicket({ oggetto: e.target.value })}
              disabled={!editableGen}
              placeholder="Es. Guasto avvolgitore linea 3 · sostituzione pignone"
              data-testid="f1-oggetto"
            />
          </div>
          <div className="mb-6">
            <Label>Descrizione richiesta *</Label>
            <Textarea rows={3} value={ticket.descrizione||""} onChange={e => setTicket({ ...ticket, descrizione: e.target.value })} onBlur={e => saveTicket({ descrizione: e.target.value })} disabled={!editableGen} data-testid="f1-descrizione" placeholder="Descrivi in breve la segnalazione del cliente…" />
          </div>

          <div className="section-h">Ingresso e natura</div>
          <div className="grid md:grid-cols-5 gap-3 mb-6">
            <div><Label>Data richiesta</Label>
              <Input
                type="date"
                value={(ticket.data_richiesta || "").slice(0, 10)}
                onChange={e => {
                  const d = e.target.value;
                  const iso = d ? new Date(d + "T00:00:00").toISOString() : "";
                  saveTicket({ data_richiesta: iso });
                }}
                disabled={!editableGen}
                data-testid="f1-data-richiesta"
              />
            </div>
            <div><Label>Canale</Label>
              <Select value={ticket.canale} onValueChange={v => saveTicket({ canale: v })} disabled={!editableGen}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem><SelectItem value="telefono">Telefono</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="commerciale">Commerciale</SelectItem>
                  <SelectItem value="tecnico_in_cantiere">Tecnico in cantiere</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Natura iniziale</Label>
              <Select value={ticket.natura} onValueChange={v => saveTicket({ natura: v })} disabled={!editableGen}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SW">SW</SelectItem><SelectItem value="HW">HW</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Sede responsabile</Label>
              <Select value={ticket.sede} onValueChange={v => saveTicket({ sede: v })} disabled={!editableGen}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Perugia">Perugia</SelectItem><SelectItem value="Treviso">Treviso</SelectItem><SelectItem value="Milano">Milano</SelectItem><SelectItem value="Novara">Novara</SelectItem><SelectItem value="Pieri">Pieri</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 mt-6"><Checkbox checked={ticket.urgente} onCheckedChange={v => saveTicket({ urgente: !!v })} disabled={!editableGen} data-testid="f1-urgente" /><Label>Urgente</Label></div>
          </div>

          <div className="section-h">Note interne</div>
          <div className="grid md:grid-cols-1 gap-3">
            <div>
              {isTecnico() ? <div className="text-xs text-slate-400 italic">Non visibile ai tecnici</div> :
                <Textarea rows={2} value={ticket.note_interne||""} onChange={e => setTicket({ ...ticket, note_interne: e.target.value })} onBlur={e => saveTicket({ note_interne: e.target.value })} disabled={!editableGen} placeholder="Note operative interne al service…" />}
            </div>
          </div>
        </Card></TabsContent>

        {/* ===== F2 Triage ===== */}
        <TabsContent value="f2"><Card className="card-clean p-5 mt-3">
          <FaseHeader ticket={ticket} num={2} onFaseChange={changeFase} canEdit={editableGen} />
          <div className="section-h">Divisione/i coinvolta/e *</div>
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {(data.divisioni || []).filter(d => d.attivo).map(d => {
                const on = (ticket.divisioni_coinvolte || []).includes(d.codice);
                return (
                  <button key={d.codice} type="button" disabled={!editableGen}
                    onClick={() => {
                      const cur = ticket.divisioni_coinvolte || [];
                      const next = on ? cur.filter(x => x !== d.codice) : [...cur, d.codice];
                      saveTicket({ divisioni_coinvolte: next });
                    }}
                    data-testid={`div-${d.codice}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition ${on ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}`}>
                    {d.codice} <span className="opacity-70 text-xs">· {d.descrizione}</span>
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-slate-500 mt-2">Obbligatorio prima di completare la fase 2. Modificabile solo da OWNER e SERVICE_MANAGER.</div>
          </div>
          <div className="section-h">Oggetto dell'intervento</div>
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            <div className="md:col-span-2"><Label>Descrizione tecnica</Label><Textarea rows={2} value={ticket.descr_tecnica||""} onChange={e=>setTicket({...ticket,descr_tecnica:e.target.value})} onBlur={e=>saveTicket({descr_tecnica:e.target.value})} disabled={!editableGen} data-testid="f2-descrtec" /></div>
            <div><Label>Zona impianto</Label><Input value={ticket.zona_impianto||""} onChange={e=>setTicket({...ticket,zona_impianto:e.target.value})} onBlur={e=>saveTicket({zona_impianto:e.target.value})} disabled={!editableGen} /></div>
            <div><Label>Macchina</Label><Input value={ticket.macchina||""} onChange={e=>setTicket({...ticket,macchina:e.target.value})} onBlur={e=>saveTicket({macchina:e.target.value})} disabled={!editableGen} /></div>
            <div><Label>Matricola</Label><Input value={ticket.matricola||""} onChange={e=>setTicket({...ticket,matricola:e.target.value})} onBlur={e=>saveTicket({matricola:e.target.value})} disabled={!editableGen} /></div>
            <div><Label>Commessa</Label><Input value={ticket.commessa||""} onChange={e=>setTicket({...ticket,commessa:e.target.value})} onBlur={e=>saveTicket({commessa:e.target.value})} disabled={!editableGen} /></div>
            <div><Label>Tipo gestione</Label>
              <Select value={ticket.classificazione} onValueChange={v=>saveTicket({classificazione:v})} disabled={!editableGen}>
                <SelectTrigger data-testid="f2-tipo-gestione"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Da definire">Da definire</SelectItem>
                  <SelectItem value="Economia">Economia</SelectItem>
                  <SelectItem value="Garanzia">Garanzia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Numero economia/garanzia</Label>
              <Input
                value={ticket.codice_eg||""}
                onChange={e=>setTicket({...ticket,codice_eg:e.target.value})}
                onBlur={e=>saveTicket({codice_eg:e.target.value})}
                disabled={!editableGen}
                placeholder="Es. E26-0003PG, GI25-0032PG"
                data-testid="f2-codice-eg"
              />
            </div>
            <div><Label>Fornitore coinvolto</Label>
              <Input value={ticket.fornitore||""} onChange={e=>setTicket({...ticket,fornitore:e.target.value})} onBlur={e=>saveTicket({fornitore:e.target.value})} disabled={!editableGen} placeholder="Facoltativo" data-testid="f2-fornitore" />
            </div>
            <div><Label>Tipo richiesta</Label>
              <Select value={ticket.priorita || "Media"} onValueChange={v=>saveTicket({priorita:v})} disabled={!editableGen}>
                <SelectTrigger data-testid="f2-priorita"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bassa">Bassa</SelectItem>
                  <SelectItem value="Media">Media</SelectItem>
                  <SelectItem value="Alta">Alta</SelectItem>
                  <SelectItem value="Critica">Critica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Classificazione HW/SW/DOC</Label>
              <Select value={ticket.classificazione_hwsw || "SW"} onValueChange={v=>saveTicket({classificazione_hwsw:v})} disabled={!editableGen}>
                <SelectTrigger data-testid="f2-classif-hwsw"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="HW">HW</SelectItem>
                  <SelectItem value="SW">SW</SelectItem>
                  <SelectItem value="HW+SW">HW+SW</SelectItem>
                  <SelectItem value="DOC">DOC</SelectItem>
                  <SelectItem value="Altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Fornitore coinvolto</Label>
              <Input value={ticket.fornitore||""} onChange={e=>setTicket({...ticket,fornitore:e.target.value})} onBlur={e=>saveTicket({fornitore:e.target.value})} disabled={!editableGen} placeholder="Facoltativo" data-testid="f2-fornitore" />
            </div>
            <div><Label>Tipo richiesta</Label>
              <Select value={ticket.tipo_richiesta} onValueChange={v=>saveTicket({tipo_richiesta:v})} disabled={!editableGen}>
                <SelectTrigger data-testid="f2-tipo-richiesta"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Intervento">Intervento</SelectItem>
                  <SelectItem value="Modifica">Modifica</SelectItem>
                  <SelectItem value="ANALISI">ANALISI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Destinazione</Label>
              <Select value={ticket.destinazione} onValueChange={v=>saveTicket({destinazione:v})} disabled={!editableGen}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Service">Service</SelectItem><SelectItem value="Commessa impianto">Commessa impianto</SelectItem><SelectItem value="Ricambi">Ricambi</SelectItem></SelectContent>
              </Select>
            </div>
            {ticket.destinazione !== "Service" && (<>
              <div><Label>Reparto destinatario</Label><Input value={ticket.reparto_destinatario||""} onChange={e=>setTicket({...ticket,reparto_destinatario:e.target.value})} onBlur={e=>saveTicket({reparto_destinatario:e.target.value})} disabled={!editableGen} /></div>
              <div><Label>Nota di passaggio</Label><Input value={ticket.nota_passaggio||""} onChange={e=>setTicket({...ticket,nota_passaggio:e.target.value})} onBlur={e=>saveTicket({nota_passaggio:e.target.value})} disabled={!editableGen} /></div>
            </>)}
          </div>
          <div className="section-h">Accettazione condizioni economiche</div>
          <div className="grid md:grid-cols-3 gap-3">
            <Select value={ticket.accettazione_condizioni?.stato||"N/A"} onValueChange={v=>saveTicket({accettazione_condizioni:{...(ticket.accettazione_condizioni||{}),stato:v}})} disabled={!editableGen}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="N/A">Non applicabile</SelectItem><SelectItem value="Da inviare">Da inviare</SelectItem><SelectItem value="Inviata">Inviata</SelectItem><SelectItem value="Accettata">Accettata</SelectItem><SelectItem value="Rifiutata">Rifiutata</SelectItem></SelectContent>
            </Select>
            <Input type="date" value={ticket.accettazione_condizioni?.data||""} onChange={e=>saveTicket({accettazione_condizioni:{...(ticket.accettazione_condizioni||{}),data:e.target.value}})} disabled={!editableGen} />
            <BufferedInput placeholder="Nota" value={ticket.accettazione_condizioni?.nota||""} onSave={v=>saveTicket({accettazione_condizioni:{...(ticket.accettazione_condizioni||{}),nota:v}})} disabled={!editableGen} />
          </div>
        </Card></TabsContent>

        {/* ===== F3 Assegnazione ===== */}
        <TabsContent value="f3"><Card className="card-clean p-5 mt-3">
          <FaseHeader ticket={ticket} num={3} onFaseChange={changeFase} canEdit={editableGen} />
          <AssegnazioneSection ticket={ticket} interventi={interventi} tecnici={data.tecnici} onChange={load} editable={editableGen} saveTicket={saveTicket} />
        </Card></TabsContent>

        {/* ===== F4 Dati intervento ===== */}
        <TabsContent value="f4"><Card className="card-clean p-5 mt-3">
          <FaseHeader ticket={ticket} num={4} onFaseChange={changeFase} canEdit={editableGen || canEdit("rapporto")} />
          <RapportiSection ticket={ticket} rapporti={rapporti} tecnici={data.tecnici} paese={paese} cliente={cliente} stabilimento={stab} onChange={load} isTecnico={isTecnico()} myTecId={myTecnicoId()} isViewer={isViewer} profileNome={profile.nome} />
        </Card></TabsContent>

        {/* ===== F5 Consuntivo ===== */}
        <TabsContent value="f5"><Card className="card-clean p-5 mt-3">
          <FaseHeader ticket={ticket} num={5} onFaseChange={changeFase} canEdit={editableGen} />
          <ConsuntivoSection ticket={ticket} consuntivi={consuntivi} rapporti={rapporti} paese={paese} onChange={load} editable={editableGen} />
        </Card></TabsContent>

        {/* ===== F6 Conferma cliente ===== */}
        <TabsContent value="f6"><Card className="card-clean p-5 mt-3">
          <FaseHeader ticket={ticket} num={6} onFaseChange={changeFase} canEdit={editableGen} />
          <ConfermaSection ticket={ticket} consuntivi={consuntivi} conferme={conferme} onChange={load} editable={editableGen} />
        </Card></TabsContent>

        {/* ===== F7 Fatturazione ===== */}
        <TabsContent value="f7"><Card className="card-clean p-5 mt-3">
          <FaseHeader ticket={ticket} num={7} onFaseChange={changeFase} canEdit={editableFatt || editableGen} />
          <FatturazioneSection ticket={ticket} fatture={fatture} totConsuntivo={totConsuntivo} totFatturato={totFatturato} statoFatt={statoFatt} onChange={load} editable={editableFatt || editableGen} />
        </Card></TabsContent>

        {/* ===== F8 Chiusura ===== */}
        <TabsContent value="f8"><Card className="card-clean p-5 mt-3">
          <FaseHeader ticket={ticket} num={8} onFaseChange={changeFase} canEdit={canClose()} />
          <ChiusuraSection ticket={ticket} rapporti={rapporti} consuntivi={consuntivi} conferme={conferme} fatture={fatture} documenti={documenti} onChange={load} canClose={canClose()} profileNome={profile.nome} />
        </Card></TabsContent>

        {/* ===== Aggiornamenti (cronologia) ===== */}
        <TabsContent value="agg"><Card className="card-clean p-5 mt-3">
          <AggiornamentiSection ticket={ticket} canEdit={canEdit("ticket")} />
        </Card></TabsContent>

        {/* ===== Documenti ===== */}
        <TabsContent value="doc"><Card className="card-clean p-5 mt-3">
          <DocumentiSection ticketId={ticket.id} documenti={documenti} rapporti={rapporti} onChange={load} editable={editableGen || canEdit("rapporto")} />
        </Card></TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Sub-sections ============

function AggiornamentiSection({ ticket, canEdit }) {
  const [items, setItems] = useState([]);
  const [testo, setTesto] = useState("");
  const [saving, setSaving] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const list = await api.get(`/tickets/${ticket.id}/aggiornamenti`);
      setItems(list || []);
    } catch { /* silent */ }
  }, [ticket.id]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const t = testo.trim();
    if (!t) { toast.error("Scrivi qualcosa prima di salvare"); return; }
    setSaving(true);
    try {
      await api.post(`/tickets/${ticket.id}/aggiornamenti`, { testo: t });
      setTesto("");
      toast.success("Aggiornamento salvato");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="text-sm text-slate-600 mb-3">
        Cronologia degli aggiornamenti del ticket. Ogni nuova voce conserva data, ora, autore e testo — <strong>non sovrascrive mai</strong> quelle precedenti. La "Prossima azione" (fase 3) rappresenta ciò che resta da fare; qui invece si registra ciò che è già accaduto.
      </div>

      {canEdit && (
        <div className="border rounded p-3 mb-4 bg-slate-50">
          <Label>Nuovo aggiornamento</Label>
          <Textarea
            rows={3}
            value={testo}
            onChange={e => setTesto(e.target.value)}
            placeholder="Es. Parlato con Loris — invia preventivo il 10/03. In attesa PO cliente…"
            data-testid="agg-input"
          />
          <div className="flex justify-end mt-2">
            <Button onClick={send} disabled={saving || !testo.trim()} data-testid="agg-save" className="bg-blue-700 hover:bg-blue-800">
              <Plus className="w-4 h-4 mr-1" /> Registra aggiornamento
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="agg-list">
        {items.length === 0 && (
          <div className="text-center text-slate-500 py-8">Nessun aggiornamento registrato per questo ticket.</div>
        )}
        {items.map(a => (
          <div key={a.id} className="border rounded p-3 bg-white" data-testid={`agg-item-${a.id}`}>
            <div className="flex justify-between items-center flex-wrap gap-2 mb-1">
              <div className="font-semibold text-slate-800 text-sm">{a.autore_nome || "—"}</div>
              <div className="text-xs text-slate-500 font-mono">{fmtDateTime(a.timestamp)}</div>
            </div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{a.testo}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AssegnazioneSection({ ticket, interventi, tecnici, onChange, editable, saveTicket }) {
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ tecnici_ids: [], data_pianificata: "", note: "", materiale_necessario: "" });
  // Local buffers for text fields so quick typing doesn't drop characters while saveTicket round-trips.
  const [prossimaBuf, setProssimaBuf] = useState(ticket.prossima_azione || "");
  const [scadenzaBuf, setScadenzaBuf] = useState(ticket.scadenza || "");
  useEffect(() => { setProssimaBuf(ticket.prossima_azione || ""); }, [ticket.id, ticket.prossima_azione]);
  useEffect(() => { setScadenzaBuf(ticket.scadenza || ""); }, [ticket.id, ticket.scadenza]);
  const create = async () => {
    if (!form.data_pianificata) { toast.error("Data richiesta"); return; }
    await api.post("/interventi", { ticket_id: ticket.id, ...form });
    setOpenNew(false); setForm({ tecnici_ids: [], data_pianificata: "", note: "", materiale_necessario: "" });
    onChange(); toast.success("Intervento pianificato");
  };
  const del = async (id) => { await api.del(`/interventi/${id}`); onChange(); };
  return (<>
    <div className="grid md:grid-cols-2 gap-3 mb-4">
      <div>
        <Label>Responsabile interno della pendenza</Label>
        <Combobox
          value={(tecnici || []).find(x => (x.nome || "") === (ticket.responsabile || ""))?.id || ""}
          onChange={(id) => {
            const t = (tecnici || []).find(x => x.id === id);
            saveTicket({ responsabile: t?.nome || "" });
          }}
          options={(tecnici || []).map(x => ({ value: x.id, label: `${x.nome}${x.tipo ? ` · ${x.tipo}` : ""}` }))}
          placeholder={ticket.responsabile ? ticket.responsabile : "Cerca tecnico…"}
          searchPlaceholder="Cerca tecnico…"
          emptyText="Nessun tecnico trovato — aggiungilo da Anagrafiche"
          disabled={!editable}
          data-testid="f3-responsabile"
        />
        <div className="text-xs text-slate-500 mt-1">Distinto dai tecnici che eseguono l'attività. Elenco preso da Anagrafiche → Tecnici.</div>
      </div>
      <div>
        <Label>Prossima azione</Label>
        <Input
          value={prossimaBuf}
          onChange={e => setProssimaBuf(e.target.value)}
          onBlur={() => { if (prossimaBuf !== (ticket.prossima_azione || "")) saveTicket({ prossima_azione: prossimaBuf }); }}
          disabled={!editable}
          data-testid="f3-prossima-azione"
        />
      </div>
      <div>
        <Label>Data prevista prossima azione</Label>
        <Input
          type="date"
          value={ticket.data_prossima_azione||""}
          onChange={e => saveTicket({ data_prossima_azione: e.target.value })}
          disabled={!editable}
          data-testid="f3-data-prossima-azione"
        />
      </div>
      <div>
        <Label>Data prossimo aggiornamento</Label>
        <Input
          type="date"
          value={ticket.data_prossimo_aggiornamento||""}
          onChange={e => saveTicket({ data_prossimo_aggiornamento: e.target.value })}
          disabled={!editable}
          data-testid="f3-data-prossimo-agg"
        />
      </div>
      <div>
        <Label>Scadenza (legacy)</Label>
        <Input
          type="date"
          value={scadenzaBuf}
          onChange={e => setScadenzaBuf(e.target.value)}
          onBlur={() => { if (scadenzaBuf !== (ticket.scadenza || "")) saveTicket({ scadenza: scadenzaBuf }); }}
          disabled={!editable}
          data-testid="f3-scadenza"
        />
      </div>
    </div>
    <div className="flex justify-between items-center mb-2"><h3 className="font-semibold">Interventi pianificati</h3>{editable && <Button size="sm" onClick={() => setOpenNew(true)} data-testid="btn-add-intervento"><Plus className="w-4 h-4 mr-1" /> Nuovo</Button>}</div>
    <Table><TableHeader><TableRow className="bg-slate-50"><TableHead>Data</TableHead><TableHead>Tecnici</TableHead><TableHead>Note</TableHead><TableHead>Materiale necessario</TableHead>{editable && <TableHead></TableHead>}</TableRow></TableHeader>
      <TableBody>{interventi.map(iv => (
        <TableRow key={iv.id}><TableCell>{fmtDate(iv.data_pianificata)}</TableCell><TableCell>{(iv.tecnici_ids||[]).map(t=>tecnici.find(x=>x.id===t)?.nome).filter(Boolean).join(", ")}</TableCell><TableCell>{iv.note}</TableCell><TableCell>{iv.materiale_necessario}</TableCell>{editable && <TableCell><Button size="icon" variant="ghost" onClick={()=>del(iv.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>}</TableRow>))}
      {interventi.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-4">Nessun intervento pianificato</TableCell></TableRow>}</TableBody></Table>
    <Dialog open={openNew} onOpenChange={setOpenNew}><DialogContent><DialogHeader><DialogTitle>Nuovo intervento pianificato</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Data pianificata *</Label><Input type="date" value={form.data_pianificata} onChange={e=>setForm({...form,data_pianificata:e.target.value})} data-testid="int-data" /></div>
        <div><Label>Tecnici</Label>
          <div className="space-y-1 max-h-40 overflow-auto border rounded p-2">
            {tecnici.map(t => (
              <label key={t.id} className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.tecnici_ids.includes(t.id)} onCheckedChange={v => setForm({...form, tecnici_ids: v ? [...form.tecnici_ids, t.id] : form.tecnici_ids.filter(x=>x!==t.id)})} />
                {t.nome} · <span className="text-xs text-slate-500">{t.sede} {t.tipo}</span>
              </label>
            ))}
          </div>
        </div>
        <div><Label>Note organizzative</Label><Textarea value={form.note} onChange={e=>setForm({...form,note:e.target.value})} /></div>
        <div><Label>Materiale necessario</Label><Textarea value={form.materiale_necessario} onChange={e=>setForm({...form,materiale_necessario:e.target.value})} /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={()=>setOpenNew(false)}>Annulla</Button><Button onClick={create} data-testid="int-save">Salva</Button></DialogFooter>
    </DialogContent></Dialog>
  </>);
}

function RapportiSection({ ticket, rapporti, tecnici, paese, cliente, stabilimento, onChange, isTecnico, myTecId, isViewer, profileNome }) {
  const [openNew, setOpenNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const myRapporti = isTecnico ? rapporti.filter(r => r.tecnico_id === myTecId) : rapporti;

  const create = async (tec_id) => {
    const r = await api.post("/rapporti", { ticket_id: ticket.id, tecnico_id: tec_id, giornate: [] });
    setOpenNew(false); onChange(); setSelected(r); toast.success("Rapporto creato");
  };

  return (<>
    <div className="flex justify-between items-center mb-2"><h3 className="font-semibold">Rapporti tecnici</h3>
      {!isViewer && <Button size="sm" onClick={()=>setOpenNew(true)} data-testid="btn-new-rapporto"><Plus className="w-4 h-4 mr-1" /> Nuovo rapporto</Button>}
    </div>
    <div className="space-y-2">
      {myRapporti.map(r => {
        const tec = tecnici.find(t => t.id === r.tecnico_id);
        return (
          <div key={r.id} className={`border rounded-lg p-3 ${r.annullato ? "bg-red-50 opacity-70" : "bg-white"}`} data-testid={`rapporto-${r.id}`}>
            <div className="flex justify-between items-start gap-2 flex-wrap">
              <div>
                <div className="font-semibold">{tec?.nome} {r.annullato && <Badge variant="destructive" className="ml-2">Annullato</Badge>} {r.bloccato && !r.annullato && <Badge variant="outline" className="ml-2"><Lock className="w-3 h-3 mr-1" />Confermato</Badge>}</div>
                <div className="text-xs text-slate-500">{r.giornate?.length||0} giornate · esito: {r.esito} · {r.modalita_prevalente}</div>
                {r.copia_da && <div className="text-xs text-blue-700">Copia correzione</div>}
                {r.motivo_annullamento && <div className="text-xs text-red-700">Annullato: {r.motivo_annullamento}</div>}
              </div>
              <Button variant="outline" size="sm" onClick={()=>setSelected(r)} data-testid={`rapp-open-${r.id}`}>Apri</Button>
            </div>
          </div>
        );
      })}
      {myRapporti.length === 0 && <div className="text-center text-slate-500 py-6">Nessun rapporto</div>}
    </div>
    <Dialog open={openNew} onOpenChange={setOpenNew}><DialogContent><DialogHeader><DialogTitle>Nuovo rapporto</DialogTitle></DialogHeader>
      <div className="space-y-2 text-sm">Scegli il tecnico:</div>
      <div className="grid grid-cols-1 gap-2 max-h-64 overflow-auto">
        {tecnici.filter(t => !isTecnico || t.id === myTecId).map(t => (
          <button key={t.id} onClick={() => create(t.id)} className="text-left border rounded p-2 hover:bg-blue-50" data-testid={`new-rapp-tec-${t.id}`}>
            <div className="font-semibold">{t.nome}</div><div className="text-xs text-slate-500">{t.sede} · {t.tipo} · {t.competenza}</div>
          </button>
        ))}
      </div>
    </DialogContent></Dialog>
    {selected && <RapportoEditor rapporto={selected} tecnico={tecnici.find(t=>t.id===selected.tecnico_id)} ticket={ticket} paese={paese} cliente={cliente} stabilimento={stabilimento} onClose={() => setSelected(null)} onChange={onChange} readOnly={isViewer || (isTecnico && selected.tecnico_id !== myTecId)} />}
  </>);
}

function RapportoEditor({ rapporto, tecnico, ticket, paese, cliente, stabilimento, onClose, onChange, readOnly }) {
  const [r, setR] = useState(rapporto);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [annullaOpen, setAnnullaOpen] = useState(false);
  const [motivoAnn, setMotivoAnn] = useState("");
  const [firma, setFirma] = useState({ nome: "", ruolo: "", svg: "", cliente_assente: false, motivo: "" });

  const locked = r.bloccato || r.annullato || readOnly;

  const update = (patch) => setR({ ...r, ...patch });
  const addGiornata = () => update({ giornate: [...(r.giornate||[]), { data: "", modalita: "In loco", ore_lavoro: 0, ore_viaggio: 0, km: 0, natura: "SW", descrizione: "" }] });
  const setG = (idx, patch) => { const g = [...r.giornate]; g[idx] = { ...g[idx], ...patch }; if (patch.modalita === "Da remoto") { g[idx].ore_viaggio = 0; g[idx].km = 0; } update({ giornate: g }); };
  const delG = (idx) => update({ giornate: r.giornate.filter((_,i)=>i!==idx) });

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const M = 15;
    let y = 15;

    // Header brand
    doc.setFillColor(30, 64, 175);
    doc.rect(0, 0, W, 22, "F");
    doc.setTextColor(255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Emmepi Group", M, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Service · Rapporto di intervento (documento dimostrativo)", M, 18);
    y = 30;

    // Ticket info block
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`Rapporto ticket ${ticket.numero}${ticket.codice_eg ? " · " + ticket.codice_eg : ""}`, M, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Cliente: ${cliente?.ragione_sociale || "—"}`, M, y); y += 5;
    doc.text(`Stabilimento: ${stabilimento?.nome || "—"} (${stabilimento?.paese || ""})`, M, y); y += 5;
    doc.text(`Tecnico: ${tecnico?.nome || "—"}`, M, y); y += 5;
    doc.text(`Modalità: ${r.modalita_prevalente} · Esito: ${r.esito}`, M, y); y += 8;

    // Giornate table
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Giornate di lavoro", M, y); y += 5;
    doc.setDrawColor(226, 232, 240); doc.line(M, y, W - M, y); y += 3;
    doc.setFontSize(8);
    const headers = ["Data", "Modalità", "Ore lav", "Ore vgg", "Km", "Tipo", "Descrizione"];
    const cols = [22, 22, 15, 15, 15, 12, 79];
    let x = M;
    headers.forEach((h, i) => { doc.text(h, x, y); x += cols[i]; });
    y += 4;
    doc.line(M, y, W - M, y); y += 3;
    doc.setFont("helvetica", "normal");
    let totOre = 0, totVgg = 0, totKm = 0;
    (r.giornate || []).forEach(g => {
      x = M;
      const row = [g.data || "-", g.modalita || "-", String(g.ore_lavoro || 0), String(g.ore_viaggio || 0), String(g.km || 0), g.natura || "-", g.descrizione || ""];
      row.forEach((val, i) => {
        const txt = i === 6 ? doc.splitTextToSize(val, cols[i] - 2) : val;
        doc.text(txt, x, y);
        x += cols[i];
      });
      totOre += Number(g.ore_lavoro || 0); totVgg += Number(g.ore_viaggio || 0); totKm += Number(g.km || 0);
      y += Math.max(4, (Array.isArray(doc.splitTextToSize(g.descrizione || "", cols[6] - 2)) ? doc.splitTextToSize(g.descrizione || "", cols[6] - 2).length : 1) * 4);
    });
    y += 2;
    doc.setFont("helvetica", "bold");
    doc.text(`Totali: ${totOre}h lavoro · ${totVgg}h viaggio · ${totKm} km`, M, y);
    y += 8;

    // Trasferte
    doc.text("Trasferte", M, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    if (paese === "Italia") {
      doc.text(`Pernottamenti: ${r.pernottamenti || 0}   Pranzi: ${r.pranzi || 0}   Cene: ${r.cene || 0}`, M, y);
    } else {
      doc.text(`Out of pocket: € ${(r.out_of_pocket || 0).toFixed(2)}`, M, y);
    }
    y += 6;
    if (r.materiale_utilizzato) {
      doc.setFont("helvetica", "bold"); doc.text("Materiale utilizzato", M, y); y += 4;
      doc.setFont("helvetica", "normal");
      const mat = doc.splitTextToSize(r.materiale_utilizzato, W - 2 * M);
      doc.text(mat, M, y); y += mat.length * 4 + 4;
    }

    // Firma
    y += 4;
    doc.setFont("helvetica", "bold"); doc.setFontSize(10);
    doc.text("Firma cliente", M, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    if (r.cliente_assente) {
      doc.text(`Cliente assente / rifiuta la firma.`, M, y); y += 4;
      doc.text(`Motivazione: ${r.motivo_no_firma || "—"}`, M, y); y += 4;
    } else if (r.modalita_prevalente === "Da remoto") {
      doc.text("Rapporto da remoto: firma non richiesta.", M, y); y += 4;
    } else {
      doc.text(`Firmatario: ${r.firma_nome || "—"} · Ruolo: ${r.firma_ruolo || "—"}`, M, y); y += 4;
      doc.text(`Data firma: ${r.firma_data ? new Date(r.firma_data).toLocaleString("it-IT") : "—"}`, M, y); y += 4;
      if (r.firma_svg) {
        try { doc.addImage(r.firma_svg, "PNG", M, y, 60, 20); y += 22; } catch (e) { /* ignore */ }
      }
    }

    // Footer disclaimer
    doc.setFontSize(8); doc.setTextColor(148, 163, 184);
    doc.text("Firma dimostrativa — nessun valore legale · Ambiente demo Emmepi Service", M, 285);
    doc.text(`Generato il ${new Date().toLocaleString("it-IT")}`, M, 289);

    doc.save(`Rapporto_${ticket.numero}_${tecnico?.nome?.split(" ")[0] || "tecnico"}.pdf`);
    toast.success("PDF generato");
  };

  const save = async () => {
    if (r.giornate.some(g => g.ore_lavoro < 0 || g.ore_viaggio < 0 || g.km < 0)) { toast.error("Valori negativi non ammessi"); return; }
    if (r.giornate.some(g => (g.ore_lavoro * 2) % 1 !== 0 || (g.ore_viaggio * 2) % 1 !== 0)) { toast.error("Ore a incrementi di mezz'ora"); return; }
    await api.put(`/rapporti/${r.id}`, r);
    toast.success("Rapporto salvato"); onChange();
  };

  const conferma = async () => {
    if (!firma.cliente_assente && !firma.svg && r.modalita_prevalente === "In loco") { toast.error("Firma o motivazione assenza richiesta"); return; }
    if (firma.cliente_assente && !firma.motivo) { toast.error("Motivazione richiesta"); return; }
    await api.post(`/rapporti/${r.id}/conferma`, {
      firma_nome: firma.nome, firma_ruolo: firma.ruolo, firma_svg: firma.svg,
      cliente_assente: firma.cliente_assente, motivo_no_firma: firma.motivo,
    });
    toast.success("Rapporto confermato e bloccato"); setConfirmOpen(false); onChange(); onClose();
  };

  const annulla = async () => {
    if (!motivoAnn.trim()) { toast.error("Motivazione richiesta"); return; }
    await api.post(`/rapporti/${r.id}/annulla`, { motivo: motivoAnn });
    toast.success("Rapporto annullato, creata copia editabile"); setAnnullaOpen(false); onChange(); onClose();
  };

  const totOreLavoro = (r.giornate||[]).reduce((s,g)=>s+Number(g.ore_lavoro||0),0);
  const totOreViaggio = (r.giornate||[]).reduce((s,g)=>s+Number(g.ore_viaggio||0),0);
  const totKm = (r.giornate||[]).reduce((s,g)=>s+Number(g.km||0),0);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="rapporto-editor">
        <DialogHeader><DialogTitle>Rapporto · {tecnico?.nome} {r.bloccato && <Badge variant="outline" className="ml-2"><Lock className="w-3 h-3 mr-1" />Bloccato</Badge>}</DialogTitle></DialogHeader>
        {locked && <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 flex gap-2"><AlertTriangle className="w-4 h-4" /> {r.annullato ? "Rapporto annullato." : readOnly ? "Sola lettura." : "Rapporto confermato: non modificabile. Puoi annullarlo per creare una copia."}</div>}
        <div className="grid md:grid-cols-3 gap-3 mt-2">
          <div><Label>Modalità prevalente</Label><Select value={r.modalita_prevalente} onValueChange={v=>update({modalita_prevalente:v})} disabled={locked}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="In loco">In loco</SelectItem><SelectItem value="Da remoto">Da remoto</SelectItem></SelectContent></Select></div>
          <div><Label>Esito</Label><Select value={r.esito} onValueChange={v=>update({esito:v})} disabled={locked}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Risolto">Risolto</SelectItem><SelectItem value="Parzialmente risolto">Parzialmente risolto</SelectItem><SelectItem value="Non risolto">Non risolto</SelectItem></SelectContent></Select></div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2"><h4 className="font-semibold">Giornate</h4>{!locked && <Button size="sm" onClick={addGiornata} data-testid="add-giornata"><Plus className="w-4 h-4 mr-1" /> Giornata</Button>}</div>
          <div className="space-y-2">
            {r.giornate?.map((g, idx) => (
              <div key={idx} className="grid grid-cols-2 md:grid-cols-7 gap-2 border rounded p-2 items-start">
                <div><Label className="text-xs">Data</Label><Input type="date" value={g.data} onChange={e=>setG(idx,{data:e.target.value})} disabled={locked} data-testid={`g-data-${idx}`} /></div>
                <div><Label className="text-xs">Modalità</Label><Select value={g.modalita} onValueChange={v=>setG(idx,{modalita:v})} disabled={locked}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="In loco">In loco</SelectItem><SelectItem value="Da remoto">Da remoto</SelectItem></SelectContent></Select></div>
                <div><Label className="text-xs">Ore lavoro</Label><Input type="number" step="0.5" min="0" value={g.ore_lavoro} onChange={e=>setG(idx,{ore_lavoro:parseFloat(e.target.value)||0})} disabled={locked} data-testid={`g-lavoro-${idx}`} /></div>
                <div><Label className="text-xs">Ore viaggio</Label><Input type="number" step="0.5" min="0" value={g.ore_viaggio} onChange={e=>setG(idx,{ore_viaggio:parseFloat(e.target.value)||0})} disabled={locked || g.modalita==="Da remoto"} /></div>
                <div><Label className="text-xs">Km</Label><Input type="number" min="0" value={g.km} onChange={e=>setG(idx,{km:parseFloat(e.target.value)||0})} disabled={locked || g.modalita==="Da remoto"} /></div>
                <div><Label className="text-xs">Natura</Label><Select value={g.natura} onValueChange={v=>setG(idx,{natura:v})} disabled={locked}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="SW">SW</SelectItem><SelectItem value="HW">HW</SelectItem></SelectContent></Select></div>
                <div className="col-span-2 md:col-span-7"><Label className="text-xs">Descrizione</Label><div className="flex gap-1"><Textarea rows={2} value={g.descrizione} onChange={e=>setG(idx,{descrizione:e.target.value})} disabled={locked} className="flex-1" />{!locked && <Button size="icon" variant="ghost" onClick={()=>delG(idx)}><Trash2 className="w-4 h-4 text-red-600" /></Button>}</div></div>
              </div>
            ))}
            {(!r.giornate || r.giornate.length===0) && <div className="text-center text-slate-500 py-3 border rounded">Nessuna giornata</div>}
          </div>
          <div className="text-sm text-slate-600 mt-2">Totali: {totOreLavoro}h lavoro · {totOreViaggio}h viaggio · {totKm} km</div>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div><Label>Materiale utilizzato</Label><Textarea value={r.materiale_utilizzato} onChange={e=>update({materiale_utilizzato:e.target.value})} disabled={locked} /></div>
          <div className="space-y-2">
            <Label>Trasferte {paese === "Italia" ? "(Italia)" : "(Estero)"}</Label>
            {paese === "Italia" ? (
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Pernotti</Label><Input type="number" min="0" value={r.pernottamenti} onChange={e=>update({pernottamenti:parseInt(e.target.value)||0})} disabled={locked} /></div>
                <div><Label className="text-xs">Pranzi</Label><Input type="number" min="0" value={r.pranzi} onChange={e=>update({pranzi:parseInt(e.target.value)||0})} disabled={locked} /></div>
                <div><Label className="text-xs">Cene</Label><Input type="number" min="0" value={r.cene} onChange={e=>update({cene:parseInt(e.target.value)||0})} disabled={locked} /></div>
              </div>
            ) : (
              <div><Label className="text-xs">Out of pocket (€)</Label><Input type="number" min="0" step="0.01" value={r.out_of_pocket} onChange={e=>update({out_of_pocket:parseFloat(e.target.value)||0})} disabled={locked} /></div>
            )}
          </div>
        </div>
        <DialogFooter className="mt-4 flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="btn-pdf-rapp"><FileText className="w-4 h-4 mr-1" /> Scarica PDF</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Stampa</Button>
          {r.bloccato && !r.annullato && !readOnly && <Button variant="outline" onClick={() => setAnnullaOpen(true)} data-testid="btn-annulla-rapp"><Ban className="w-4 h-4 mr-1" /> Annulla e ri-crea</Button>}
          {!locked && <><Button onClick={save} data-testid="btn-save-rapp"><Save className="w-4 h-4 mr-1" /> Salva</Button><Button onClick={() => setConfirmOpen(true)} data-testid="btn-conferma-rapp"><CheckCircle2 className="w-4 h-4 mr-1" /> Conferma e blocca</Button></>}
        </DialogFooter>

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Conferma rapporto {r.modalita_prevalente === "In loco" ? "· Firma cliente" : ""}</DialogTitle></DialogHeader>
            <div className="text-sm space-y-2">
              <div className="bg-slate-50 p-2 rounded text-xs">Riepilogo: {r.giornate?.length||0} giornate · {totOreLavoro}h lavoro · esito {r.esito}</div>
              {r.modalita_prevalente === "In loco" && !firma.cliente_assente && (<>
                <div><Label>Nome firmatario</Label><Input value={firma.nome} onChange={e=>setFirma({...firma,nome:e.target.value})} data-testid="firma-nome" /></div>
                <div><Label>Ruolo</Label><Input value={firma.ruolo} onChange={e=>setFirma({...firma,ruolo:e.target.value})} /></div>
                <div><Label>Firma</Label><SignaturePad value={firma.svg} onChange={v=>setFirma({...firma,svg:v})} /></div>
              </>)}
              {r.modalita_prevalente === "In loco" && (
                <div className="flex items-center gap-2 pt-2 border-t"><Checkbox checked={firma.cliente_assente} onCheckedChange={v=>setFirma({...firma,cliente_assente:!!v})} data-testid="firma-assente" /><Label>Cliente assente / rifiuta la firma</Label></div>
              )}
              {firma.cliente_assente && <div><Label>Motivazione</Label><Textarea value={firma.motivo} onChange={e=>setFirma({...firma,motivo:e.target.value})} /></div>}
              {r.modalita_prevalente === "Da remoto" && <div className="text-slate-600 text-sm">Rapporto da remoto: nessuna firma richiesta.</div>}
            </div>
            <DialogFooter><Button variant="outline" onClick={()=>setConfirmOpen(false)}>Annulla</Button><Button onClick={conferma} data-testid="firma-conferma">Conferma e blocca</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={annullaOpen} onOpenChange={setAnnullaOpen}>
          <DialogContent><DialogHeader><DialogTitle>Annulla rapporto</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">L'originale resta consultabile. Verrà creata una copia modificabile.</p>
            <div><Label>Motivazione *</Label><Textarea value={motivoAnn} onChange={e=>setMotivoAnn(e.target.value)} data-testid="ann-motivo" /></div>
            <DialogFooter><Button variant="outline" onClick={()=>setAnnullaOpen(false)}>Annulla</Button><Button variant="destructive" onClick={annulla} data-testid="ann-confirm">Annulla e crea copia</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function ConsuntivoSection({ ticket, consuntivi, rapporti, paese, onChange, editable }) {
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const create = async (payload) => {
    const c = await api.post("/consuntivi", { ticket_id: ticket.id, ...payload });
    setEditing(c); setOpenNew(false); onChange();
  };
  const saveT = async (patch) => { await api.put(`/tickets/${ticket.id}`, patch); onChange(); };
  return (<>
    <div className="border rounded p-3 mb-4 bg-slate-50">
      <div className="font-semibold mb-2 text-sm">Riepilogo rapido (inserimento manuale)</div>
      <div className="grid md:grid-cols-4 gap-3">
        <div className="flex items-center gap-2 mt-6">
          <Checkbox checked={!!ticket.consuntivo_effettuato} onCheckedChange={v => saveT({ consuntivo_effettuato: !!v })} disabled={!editable} data-testid="f5-cons-effettuato" />
          <Label>Consuntivo effettuato</Label>
        </div>
        <div><Label>Data consuntivo</Label><BufferedInput type="date" value={ticket.data_consuntivo||""} onSave={v => saveT({ data_consuntivo: v })} disabled={!editable} data-testid="f5-data-consuntivo" /></div>
        <div><Label>Importo senza IVA (manuale)</Label><BufferedInput numeric placeholder="0,00" value={ticket.importo_consuntivo_manuale} onSave={v => saveT({ importo_consuntivo_manuale: v })} disabled={!editable} data-testid="f5-importo-manuale" /></div>
        <div className="md:col-span-1"><Label>Note</Label><BufferedInput value={ticket.note_consuntivo||""} onSave={v=>saveT({note_consuntivo:v})} disabled={!editable} data-testid="f5-note-cons" /></div>
      </div>
    </div>
    <div className="flex justify-between items-center mb-2"><h3 className="font-semibold">Consuntivi (dettaglio con tariffe)</h3>{editable && <Button size="sm" onClick={()=>setOpenNew(true)} data-testid="btn-new-consuntivo"><Plus className="w-4 h-4 mr-1" /> Nuovo consuntivo</Button>}</div>
    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">Calcolo dimostrativo — regole tariffarie da validare</div>
    <div className="space-y-2">
      {consuntivi.map(c => {
        const tot = (c.righe||[]).reduce((s,r)=>s+Number(r.totale||0),0);
        return (
          <div key={c.id} className="border rounded p-3">
            <div className="flex justify-between items-start flex-wrap gap-2">
              <div>
                <div className="font-semibold">{c.titolo || "(senza titolo)"} · v{c.versione}</div>
                <div className="text-xs text-slate-500">Tariffario: {c.tariffario} · Stato: <span className={c.stato==="confermato"?"font-semibold text-blue-700":"text-amber-700"}>{c.stato}</span></div>
              </div>
              <div className="text-right">
                <div className="font-semibold">{fmtCurrency(tot)}</div>
                <Button size="sm" variant="outline" onClick={()=>setEditing(c)}>Apri</Button>
              </div>
            </div>
          </div>
        );
      })}
      {consuntivi.length === 0 && <div className="text-center text-slate-500 py-4">Nessun consuntivo</div>}
    </div>
    {openNew && <NewConsuntivoDialog rapporti={rapporti} paese={paese} onClose={()=>setOpenNew(false)} onSave={create} />}
    {editing && <ConsuntivoEditor consuntivo={editing} onClose={()=>setEditing(null)} onChange={()=>{onChange(); setEditing(null);}} editable={editable} />}
  </>);
}

function NewConsuntivoDialog({ rapporti, paese, onClose, onSave }) {
  const [titolo, setTitolo] = useState("");
  const [tariffario, setTariffario] = useState(paese === "Italia" ? "Italia" : "Estero");
  const [selected, setSelected] = useState([]);
  const genRighe = () => {
    const T = TARIFFE[tariffario];
    let oreSW = 0, oreHW = 0, oreV = 0, km = 0, pern=0, pran=0, cen=0, oop=0;
    rapporti.filter(r => selected.includes(r.id) && !r.annullato).forEach(r => {
      (r.giornate||[]).forEach(g => {
        if (g.natura === "SW") oreSW += Number(g.ore_lavoro||0); else oreHW += Number(g.ore_lavoro||0);
        oreV += Number(g.ore_viaggio||0); km += Number(g.km||0);
      });
      pern += r.pernottamenti||0; pran += r.pranzi||0; cen += r.cene||0; oop += r.out_of_pocket||0;
    });
    const righe = [];
    if (oreSW) righe.push({desc:"Ore lavoro SW",qta:oreSW,unita:"h",prezzo:T.work_sw,totale:oreSW*T.work_sw});
    if (oreHW) righe.push({desc:"Ore lavoro HW",qta:oreHW,unita:"h",prezzo:T.work_hw,totale:oreHW*T.work_hw});
    if (oreV) righe.push({desc:"Ore viaggio",qta:oreV,unita:"h",prezzo:T.travel,totale:oreV*T.travel});
    if (km) righe.push({desc:"Km",qta:km,unita:"km",prezzo:T.km,totale:km*T.km});
    if (pern) righe.push({desc:"Pernottamenti",qta:pern,unita:"n",prezzo:T.pernotto,totale:pern*T.pernotto});
    if (pran) righe.push({desc:"Pranzi",qta:pran,unita:"n",prezzo:T.pranzo,totale:pran*T.pranzo});
    if (cen) righe.push({desc:"Cene",qta:cen,unita:"n",prezzo:T.cena,totale:cen*T.cena});
    if (oop) righe.push({desc:"Out of pocket",qta:1,unita:"€",prezzo:oop,totale:oop});
    return righe;
  };
  const save = () => onSave({ titolo, tariffario, rapporti_inclusi: selected, righe: genRighe() });
  return (
    <Dialog open={true} onOpenChange={onClose}><DialogContent className="max-w-2xl" data-testid="new-cons-dialog"><DialogHeader><DialogTitle>Nuovo consuntivo</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Titolo</Label><Input value={titolo} onChange={e=>setTitolo(e.target.value)} data-testid="cons-titolo" /></div>
        <div><Label>Tariffario</Label><Select value={tariffario} onValueChange={setTariffario}><SelectTrigger data-testid="cons-tariff"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Italia">Italia (dimostrativo)</SelectItem><SelectItem value="Estero">Estero (dimostrativo)</SelectItem></SelectContent></Select></div>
        <div><Label>Rapporti da includere</Label>
          <div className="space-y-1 max-h-48 overflow-auto border rounded p-2">
            {rapporti.filter(r=>!r.annullato && r.bloccato).map(r => (
              <label key={r.id} className="flex items-center gap-2 text-sm">
                <Checkbox checked={selected.includes(r.id)} onCheckedChange={v => setSelected(v ? [...selected,r.id] : selected.filter(x=>x!==r.id))} />
                Rapporto {r.id.slice(0,6)} · {r.giornate?.length||0}g · esito {r.esito}
              </label>
            ))}
            {rapporti.filter(r=>!r.annullato&&r.bloccato).length===0 && <div className="text-xs text-slate-500">Nessun rapporto confermato</div>}
          </div>
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={save} data-testid="cons-create">Crea bozza</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function ConsuntivoEditor({ consuntivo, onClose, onChange, editable }) {
  const [c, setC] = useState(consuntivo);
  const locked = c.stato === "confermato" || !editable;
  const setRiga = (i, patch) => { const rg = [...c.righe]; rg[i] = {...rg[i], ...patch}; if (patch.qta !== undefined || patch.prezzo !== undefined) rg[i].totale = Number(rg[i].qta||0) * Number(rg[i].prezzo||0); setC({...c, righe: rg}); };
  const addRiga = () => setC({...c, righe: [...c.righe, {desc:"",qta:0,unita:"h",prezzo:0,totale:0}]});
  const delRiga = (i) => setC({...c, righe: c.righe.filter((_,x)=>x!==i)});
  const tot = c.righe.reduce((s,r)=>s+Number(r.totale||0),0);
  const save = async () => { await api.put(`/consuntivi/${c.id}`, c); toast.success("Salvato"); onChange(); };
  const conferma = async () => { await api.post(`/consuntivi/${c.id}/conferma`); toast.success("Consuntivo confermato"); onChange(); };
  return (
    <Dialog open={true} onOpenChange={onClose}><DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto"><DialogHeader><DialogTitle>Consuntivo v{c.versione} {locked && <Badge variant="outline" className="ml-2"><Lock className="w-3 h-3 mr-1" />{c.stato}</Badge>}</DialogTitle></DialogHeader>
      <div className="print:hidden text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 mb-2">Calcolo dimostrativo — regole tariffarie da validare</div>
      <div><Label>Titolo</Label><Input value={c.titolo} onChange={e=>setC({...c,titolo:e.target.value})} disabled={locked} /></div>
      <Table className="mt-3"><TableHeader><TableRow className="bg-slate-50"><TableHead>Descrizione</TableHead><TableHead className="w-24">Qta</TableHead><TableHead className="w-24">Unità</TableHead><TableHead className="w-32">Prezzo</TableHead><TableHead className="w-32 text-right">Totale</TableHead>{!locked&&<TableHead className="w-12"></TableHead>}</TableRow></TableHeader>
        <TableBody>
          {c.righe.map((r,i) => (
            <TableRow key={i}>
              <TableCell><Input value={r.desc} onChange={e=>setRiga(i,{desc:e.target.value})} disabled={locked} /></TableCell>
              <TableCell><Input type="number" step="0.01" value={r.qta} onChange={e=>setRiga(i,{qta:parseFloat(e.target.value)||0})} disabled={locked} /></TableCell>
              <TableCell><Input value={r.unita} onChange={e=>setRiga(i,{unita:e.target.value})} disabled={locked} /></TableCell>
              <TableCell><Input type="number" step="0.01" value={r.prezzo} onChange={e=>setRiga(i,{prezzo:parseFloat(e.target.value)||0})} disabled={locked} /></TableCell>
              <TableCell className="text-right font-semibold">{fmtCurrency(r.totale)}</TableCell>
              {!locked && <TableCell><Button size="icon" variant="ghost" onClick={()=>delRiga(i)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!locked && <Button size="sm" variant="outline" onClick={addRiga} className="mt-2"><Plus className="w-4 h-4 mr-1" /> Riga</Button>}
      <div className="text-right text-xl font-bold mt-3">Totale: {fmtCurrency(tot)}</div>
      <DialogFooter className="print:hidden">
        <Button variant="outline" onClick={onClose}>Chiudi</Button>
        <Button variant="outline" onClick={()=>window.print()}><Printer className="w-4 h-4 mr-1" /> Stampa/PDF</Button>
        {!locked && <><Button onClick={save} data-testid="cons-save"><Save className="w-4 h-4 mr-1" /> Salva</Button><Button onClick={conferma} data-testid="cons-conferma"><CheckCircle2 className="w-4 h-4 mr-1" /> Conferma</Button></>}
      </DialogFooter>
    </DialogContent></Dialog>
  );
}

function ConfermaSection({ ticket, consuntivi, conferme, onChange, editable }) {
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ consuntivo_id: "", versione: 1, data_invio: "", stato: "In attesa", po_cliente: "", data_conferma: "", note: "" });
  // Buffer locali per i campi F6 — evitano il drop di caratteri in digitazione rapida.
  const [poBuf, setPoBuf] = useState(ticket.po_cliente_ticket || "");
  const [noteBuf, setNoteBuf] = useState(ticket.note_conferma || "");
  useEffect(() => { setPoBuf(ticket.po_cliente_ticket || ""); }, [ticket.id, ticket.po_cliente_ticket]);
  useEffect(() => { setNoteBuf(ticket.note_conferma || ""); }, [ticket.id, ticket.note_conferma]);
  const create = async () => {
    const cons = consuntivi.find(c => c.id === form.consuntivo_id);
    await api.post("/conferme", { ticket_id: ticket.id, ...form, versione: cons?.versione || 1 });
    setOpenNew(false); onChange(); toast.success("Conferma registrata (demo, nessuna email inviata)");
  };
  const upd = async (id, patch) => { await api.put(`/conferme/${id}`, patch); onChange(); };
  const saveT = async (patch) => { await api.put(`/tickets/${ticket.id}`, patch); onChange(); };
  return (<>
    <div className="border rounded p-3 mb-4 bg-slate-50">
      <div className="font-semibold mb-2 text-sm">Riepilogo rapido (inserimento manuale)</div>
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label>PO cliente (livello ticket)</Label>
          <Input
            value={poBuf}
            onChange={e => setPoBuf(e.target.value)}
            onBlur={() => { if (poBuf !== (ticket.po_cliente_ticket || "")) saveT({ po_cliente_ticket: poBuf }); }}
            disabled={!editable}
            data-testid="f6-po-cliente"
            placeholder="Numero ordine cliente"
          />
        </div>
        <div><Label>Note conferma cliente</Label>
          <Input
            value={noteBuf}
            onChange={e => setNoteBuf(e.target.value)}
            onBlur={() => { if (noteBuf !== (ticket.note_conferma || "")) saveT({ note_conferma: noteBuf }); }}
            disabled={!editable}
            data-testid="f6-note-conferma"
          />
        </div>
      </div>
    </div>
    <div className="flex justify-between items-center mb-2"><h3 className="font-semibold">Conferme cliente</h3>{editable && <Button size="sm" onClick={()=>setOpenNew(true)} data-testid="btn-new-conferma"><Plus className="w-4 h-4 mr-1" /> Registra invio</Button>}</div>
    <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded p-2 mb-2">"Registra invio" aggiorna solo la demo. Nessuna email viene inviata.</div>
    <Table><TableHeader><TableRow className="bg-slate-50"><TableHead>Consuntivo</TableHead><TableHead>Data invio</TableHead><TableHead>Stato</TableHead><TableHead>PO cliente</TableHead><TableHead>Data conferma</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
      <TableBody>
        {conferme.map(cf => {
          const cons = consuntivi.find(c => c.id === cf.consuntivo_id);
          return (
            <TableRow key={cf.id}>
              <TableCell>{cons?.titolo} v{cf.versione}</TableCell>
              <TableCell>{fmtDate(cf.data_invio)}</TableCell>
              <TableCell>{editable ? <Select value={cf.stato} onValueChange={v=>upd(cf.id,{stato:v})}><SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="In attesa">In attesa</SelectItem><SelectItem value="Confermato">Confermato</SelectItem><SelectItem value="Da rivedere">Da rivedere</SelectItem></SelectContent></Select> : cf.stato}</TableCell>
              <TableCell>{editable ? <Input value={cf.po_cliente||""} onChange={e=>upd(cf.id,{po_cliente:e.target.value})} className="w-32" /> : cf.po_cliente}</TableCell>
              <TableCell>{editable ? <Input type="date" value={cf.data_conferma||""} onChange={e=>upd(cf.id,{data_conferma:e.target.value})} className="w-40" /> : fmtDate(cf.data_conferma)}</TableCell>
              <TableCell className="text-sm">{cf.note}</TableCell>
            </TableRow>
          );
        })}
        {conferme.length===0 && <TableRow><TableCell colSpan={6} className="text-center py-4 text-slate-500">Nessuna conferma</TableCell></TableRow>}
      </TableBody>
    </Table>
    <Dialog open={openNew} onOpenChange={setOpenNew}><DialogContent><DialogHeader><DialogTitle>Registra invio consuntivo</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Consuntivo *</Label><Select value={form.consuntivo_id} onValueChange={v=>setForm({...form,consuntivo_id:v})}><SelectTrigger data-testid="conf-cons"><SelectValue placeholder="Seleziona…" /></SelectTrigger><SelectContent>{consuntivi.filter(c=>c.stato==="confermato").map(c=><SelectItem key={c.id} value={c.id}>{c.titolo} v{c.versione}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Data invio</Label><Input type="date" value={form.data_invio} onChange={e=>setForm({...form,data_invio:e.target.value})} /></div>
        <div><Label>Note</Label><Textarea value={form.note} onChange={e=>setForm({...form,note:e.target.value})} /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={()=>setOpenNew(false)}>Annulla</Button><Button onClick={create} data-testid="conf-create">Registra</Button></DialogFooter>
    </DialogContent></Dialog>
  </>);
}

function FatturazioneSection({ ticket, fatture, totConsuntivo, totFatturato, statoFatt, onChange, editable }) {
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ numero: "", data: "", importo: 0, note: "" });
  const diff = totConsuntivo - totFatturato;
  const hasFatture = fatture.length > 0;
  const create = async () => {
    if (!form.numero || !form.data) { toast.error("Numero e data obbligatori"); return; }
    await api.post("/fatture", { ticket_id: ticket.id, ...form, importo: parseNum(form.importo) });
    setForm({ numero:"", data:"", importo:0, note:"" }); setOpenNew(false); onChange(); toast.success("Fattura registrata (emissione in Gamma esterna)");
  };
  const del = async (id) => { await api.del(`/fatture/${id}`); onChange(); };
  const saveT = async (patch) => { await api.put(`/tickets/${ticket.id}`, patch); onChange(); };
  return (<>
    <div className="border rounded p-3 mb-4 bg-slate-50">
      <div className="font-semibold mb-2 text-sm">Riepilogo rapido (inserimento manuale)</div>
      <div className="grid md:grid-cols-1 gap-3">
        <div><Label>Note amministrative</Label><BufferedInput value={ticket.note_fatturazione||""} onSave={v=>saveT({note_fatturazione:v})} disabled={!editable} data-testid="f7-note-fatt" /></div>
      </div>
    </div>
    <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded p-2 mb-3">L'emissione della fattura avviene esternamente in Gamma. Qui si registrano solo gli estremi.</div>
    <div className="grid grid-cols-3 gap-3 mb-4">
      <div className="border rounded p-3"><div className="text-xs text-slate-500">Consuntivato</div><div className="text-xl font-bold" data-testid="f7-tot-consuntivato">{fmtCurrency(totConsuntivo)}</div><div className="text-[11px] text-slate-400 mt-1">da Fase 5 (Importo senza IVA)</div></div>
      <div className="border rounded p-3"><div className="text-xs text-slate-500">Fatturato (€)</div>
        {hasFatture
          ? <><div className="text-xl font-bold" data-testid="f7-tot-fatturato">{fmtCurrency(totFatturato)}</div><div className="text-[11px] text-slate-400 mt-1">somma fatture registrate</div></>
          : <BufferedInput numeric placeholder="0,00" className="text-xl font-bold h-9 mt-1" value={ticket.importo_fatturato_manuale} onSave={v=>saveT({importo_fatturato_manuale:v})} disabled={!editable} data-testid="f7-importo-manuale" />}
      </div>
      <div className={`border rounded p-3 ${diff !== 0 ? "border-amber-300 bg-amber-50" : "border-blue-300 bg-blue-50"}`}><div className="text-xs">Differenza</div><div className="text-xl font-bold">{fmtCurrency(diff)}</div><div className="text-xs mt-1">{statoFatt}</div></div>
    </div>
    <div className="flex justify-between items-center mb-2"><h3 className="font-semibold">Fatture registrate</h3>{editable && <Button size="sm" onClick={()=>setOpenNew(true)} data-testid="btn-new-fattura"><Plus className="w-4 h-4 mr-1" /> Registra fattura</Button>}</div>
    <Table><TableHeader><TableRow className="bg-slate-50"><TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Importo</TableHead><TableHead>Note</TableHead>{editable&&<TableHead></TableHead>}</TableRow></TableHeader>
      <TableBody>{fatture.map(f => <TableRow key={f.id}><TableCell className="font-mono">{f.numero}</TableCell><TableCell>{fmtDate(f.data)}</TableCell><TableCell>{fmtCurrency(f.importo)}</TableCell><TableCell>{f.note}</TableCell>{editable&&<TableCell><Button size="icon" variant="ghost" onClick={()=>del(f.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>}</TableRow>)}
      {fatture.length===0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-slate-500">Nessuna fattura</TableCell></TableRow>}</TableBody></Table>
    <Dialog open={openNew} onOpenChange={setOpenNew}><DialogContent><DialogHeader><DialogTitle>Registra fattura</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Numero *</Label><Input value={form.numero} onChange={e=>setForm({...form,numero:e.target.value})} data-testid="fatt-numero" /></div>
        <div><Label>Data *</Label><Input type="date" value={form.data} onChange={e=>setForm({...form,data:e.target.value})} data-testid="fatt-data" /></div>
        <div><Label>Importo</Label><Input inputMode="decimal" placeholder="0,00" value={form.importo || ""} onChange={e=>setForm({...form,importo:e.target.value})} data-testid="fatt-importo" /></div>
        <div><Label>Note</Label><Textarea value={form.note} onChange={e=>setForm({...form,note:e.target.value})} /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={()=>setOpenNew(false)}>Annulla</Button><Button onClick={create} data-testid="fatt-save">Registra</Button></DialogFooter>
    </DialogContent></Dialog>
  </>);
}

function ChiusuraSection({ ticket, rapporti, consuntivi, conferme, fatture, documenti, onChange, canClose, profileNome }) {
  const [openClose, setOpenClose] = useState(false);
  const [openReopen, setOpenReopen] = useState(false);
  const [motivo, setMotivo] = useState("");
  // Buffer locali per evitare "mangia-lettere" quando saveT fa round-trip server durante digitazione veloce
  const [esitoBuf, setEsitoBuf] = useState(ticket.esito_conclusivo || "");
  const [noteBuf, setNoteBuf] = useState(ticket.note_chiusura || "");
  useEffect(() => { setEsitoBuf(ticket.esito_conclusivo || ""); }, [ticket.id, ticket.esito_conclusivo]);
  useEffect(() => { setNoteBuf(ticket.note_chiusura || ""); }, [ticket.id, ticket.note_chiusura]);
  const saveT = async (patch) => { await api.put(`/tickets/${ticket.id}`, patch); onChange(); };
  const fasiIncomplete = ticket.fasi.filter(f => f.stato !== "Completata" && f.stato !== "Non applicabile");
  const chiudi = async () => {
    // Motivazione rimossa dal dialog chiusura: viene già scritta nei campi Esito/Note nella schermata Chiusura.
    // Backend accetta motivazione opzionale; passiamo esito_conclusivo come traccia storica se presente.
    const motivazioneStorica = (esitoBuf || ticket.esito_conclusivo || "").trim() || "—";
    await api.post(`/tickets/${ticket.id}/chiudi`, { profilo: profileNome, motivazione: motivazioneStorica });
    setOpenClose(false); onChange(); toast.success("Ticket chiuso");
  };
  const riapri = async () => {
    if (!motivo) { toast.error("Motivazione richiesta"); return; }
    await api.post(`/tickets/${ticket.id}/riapri`, { profilo: profileNome, motivazione: motivo });
    setOpenReopen(false); setMotivo(""); onChange(); toast.success("Ticket riaperto");
  };
  const totCons = consuntivi.filter(c=>c.stato==="confermato").reduce((s,c)=>s+(c.righe||[]).reduce((a,r)=>a+Number(r.totale||0),0),0) || Number(ticket.importo_consuntivo_manuale||0);
  const totFatt = fatture.reduce((s,f)=>s+Number(f.importo||0),0) || Number(ticket.importo_fatturato_manuale||0);
  return (<>
    <div className="grid md:grid-cols-2 gap-3 mb-4">
      <div className="border rounded p-3">
        <div className="font-semibold mb-2">Situazione tecnica</div>
        <div className="text-sm text-slate-700">Rapporti confermati: {rapporti.filter(r=>r.bloccato&&!r.annullato).length}</div>
        <div className="text-sm text-slate-700">Documenti allegati: {documenti.length}</div>
      </div>
      <div className="border rounded p-3">
        <div className="font-semibold mb-2">Situazione amministrativa</div>
        <div className="text-sm text-slate-700">Consuntivato: {fmtCurrency(totCons)}</div>
        <div className="text-sm text-slate-700">Fatturato: {fmtCurrency(totFatt)} {totFatt < totCons && <Badge variant="outline" className="text-amber-700 border-amber-300 ml-1">Incompleto</Badge>}</div>
        <div className="text-sm text-slate-700">Conferme cliente: {conferme.filter(c=>c.stato==="Confermato").length} confermate su {conferme.length}</div>
      </div>
    </div>
    {fasiIncomplete.length > 0 && <div className="text-sm bg-amber-50 border border-amber-200 rounded p-3 mb-3"><strong>Fasi incomplete:</strong> {fasiIncomplete.map(f=>`${f.num}·${FASI_NOMI[f.num-1]}`).join(", ")}</div>}
    <div className="border rounded p-3 mb-4 bg-slate-50">
      <div className="font-semibold mb-2 text-sm">Chiusura — campi finali</div>
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label>Esito conclusivo</Label>
          <Textarea
            rows={2}
            value={esitoBuf}
            onChange={e => setEsitoBuf(e.target.value)}
            onBlur={e => { if ((e.target.value || "") !== (ticket.esito_conclusivo || "")) saveT({ esito_conclusivo: e.target.value }); }}
            disabled={!canClose}
            placeholder="Es. Risolto · Sostituito pignone · KO garanzia"
            data-testid="f8-esito"
          />
        </div>
        <div><Label>Note finali</Label>
          <Textarea
            rows={2}
            value={noteBuf}
            onChange={e => setNoteBuf(e.target.value)}
            onBlur={e => { if ((e.target.value || "") !== (ticket.note_chiusura || "")) saveT({ note_chiusura: e.target.value }); }}
            disabled={!canClose}
            placeholder="Dettagli finali, raccomandazioni, note interne di chiusura…"
            data-testid="f8-note-chiusura"
          />
        </div>
      </div>
    </div>
    <div className="flex gap-2">
      {ticket.stato !== "Chiuso" ? (
        canClose && <Button onClick={()=>setOpenClose(true)} data-testid="btn-chiudi"><Lock className="w-4 h-4 mr-1" /> Chiudi ticket</Button>
      ) : (
        canClose && <Button variant="outline" onClick={()=>setOpenReopen(true)} data-testid="btn-riapri"><RotateCcw className="w-4 h-4 mr-1" /> Riapri ticket</Button>
      )}
    </div>
    {ticket.storico_chiusure?.length > 0 && (
      <div className="mt-4 border-t pt-3">
        <div className="font-semibold mb-2">Storico chiusure/riaperture</div>
        <div className="space-y-1 text-sm">
          {ticket.storico_chiusure.map((h,i) => (
            <div key={i} className="text-slate-700"><span className="font-mono text-xs text-slate-500">{fmtDateTime(h.data)}</span> · <span className="font-semibold">{h.azione}</span> da {h.profilo} — {h.motivazione}{h.fasi_incomplete?.length>0 && <span className="text-amber-700"> · fasi incomplete: {h.fasi_incomplete.join(", ")}</span>}</div>
          ))}
        </div>
      </div>
    )}
    <Dialog open={openClose} onOpenChange={setOpenClose}><DialogContent><DialogHeader><DialogTitle>Chiusura ticket</DialogTitle></DialogHeader>
      {fasiIncomplete.length>0 && <div className="text-sm bg-amber-50 border border-amber-200 rounded p-2 mb-2">Attenzione: il ticket verrà chiuso con <strong>{fasiIncomplete.length} fasi incomplete</strong> ({fasiIncomplete.map(f=>`${f.num}·${FASI_NOMI[f.num-1]}`).join(", ")}).</div>}
      <div className="text-sm text-slate-700">Confermi la chiusura? L'esito e le note di chiusura compilate nella schermata precedente verranno salvate nello storico.</div>
      <DialogFooter><Button variant="outline" onClick={()=>setOpenClose(false)}>Annulla</Button><Button onClick={chiudi} data-testid="chiudi-confirm">Chiudi ticket</Button></DialogFooter>
    </DialogContent></Dialog>
    <Dialog open={openReopen} onOpenChange={setOpenReopen}><DialogContent><DialogHeader><DialogTitle>Riapri ticket</DialogTitle></DialogHeader>
      <div><Label>Motivazione *</Label><Textarea value={motivo} onChange={e=>setMotivo(e.target.value)} data-testid="riapri-motivo" /></div>
      <DialogFooter><Button variant="outline" onClick={()=>setOpenReopen(false)}>Annulla</Button><Button onClick={riapri} data-testid="riapri-confirm">Riapri</Button></DialogFooter>
    </DialogContent></Dialog>
  </>);
}

function DocumentiSection({ ticketId, documenti, rapporti, onChange, editable }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 10*1024*1024) { toast.error("File oltre 10 MB"); return; }
    const fd = new FormData(); fd.append("file", f); fd.append("ticket_id", ticketId);
    setUploading(true);
    try {
      await fetch(`${API}/documenti`, { method: "POST", body: fd }).then(r => { if (!r.ok) throw new Error(); });
      toast.success("Documento caricato"); onChange();
    } catch { toast.error("Errore caricamento (solo JPG, PNG, PDF ≤ 10 MB)"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const del = async (id) => { if (!window.confirm("Eliminare il documento?")) return; await api.del(`/documenti/${id}`); onChange(); };
  return (<>
    <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded p-2 mb-3">Archivio documenti interno al ticket · JPG, PNG, PDF ≤ 10 MB · Video: elemento dimostrativo, upload video non attivo.</div>
    <div className="text-xs text-slate-500 mb-3">Funzionamento offline da validare in una fase successiva.</div>
    {editable && (
      <div className="flex items-center gap-2 mb-4">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,application/pdf" onChange={handleFile} className="hidden" data-testid="upload-input" />
        <Button onClick={()=>inputRef.current?.click()} disabled={uploading} data-testid="btn-upload"><Upload className="w-4 h-4 mr-1" /> {uploading ? "Caricamento…" : "Carica documento"}</Button>
        <Button variant="outline" disabled onClick={()=>toast.info("Caricamento video escluso dalla demo")}>Video (demo)</Button>
      </div>
    )}
    <Table><TableHeader><TableRow className="bg-slate-50"><TableHead>Nome</TableHead><TableHead>Tipo</TableHead><TableHead>Dimensione</TableHead><TableHead>Caricato</TableHead><TableHead></TableHead></TableRow></TableHeader>
      <TableBody>{documenti.map(d => (
        <TableRow key={d.id}>
          <TableCell>{d.nome}</TableCell>
          <TableCell className="text-xs">{d.tipo}</TableCell>
          <TableCell className="text-xs">{(d.size/1024).toFixed(1)} KB</TableCell>
          <TableCell className="text-xs">{fmtDateTime(d.created_at)}</TableCell>
          <TableCell><div className="flex gap-1">
            <a href={`${API}/documenti/${d.id}/download`} target="_blank" rel="noreferrer" data-testid={`doc-open-${d.id}`}><Button size="icon" variant="ghost"><Download className="w-4 h-4" /></Button></a>
            {editable && <Button size="icon" variant="ghost" onClick={()=>del(d.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
          </div></TableCell>
        </TableRow>
      ))}
      {documenti.length===0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-slate-500">Nessun documento</TableCell></TableRow>}</TableBody></Table>
  </>);
}
